/**
 * The one rule for "where may the app send the user after sign-in". Only a same-origin path is
 * accepted (it starts with a single "/"); anything that could leave the site — an absolute URL,
 * a protocol-relative "//host", a backslash variant browsers normalise to "//", or a control
 * character — is refused, so a crafted ?redirect= can never become an open redirect.
 *
 * @param {unknown} value - The raw redirect value (usually a query parameter).
 * @returns {string|null} The safe path, or null when it must not be used.
 */
export function safeReturnPath(value) {
  if (typeof value !== 'string') return null;
  const path = value.trim();
  if (!path.startsWith('/') || path.startsWith('//')) return null;
  if (path.includes('\\')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(path)) return null;
  return path;
}

const RETURN_PATH_KEY = 'ava:returnPath';

function sessionStore(storage) {
  if (storage) return storage;
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch (err) {
    return null;
  }
}

/**
 * Keeps a safe return path across a sign-in that leaves the app (SSO goes through the identity
 * provider and comes back to /sso/callback without the ?redirect= query). Unsafe values are not kept.
 *
 * @param {unknown} value - The raw redirect value.
 * @param {Storage} [storage] - Defaults to sessionStorage.
 */
export function rememberReturnPath(value, storage) {
  const store = sessionStore(storage);
  if (!store) return;
  try {
    const path = safeReturnPath(value);
    if (path) store.setItem(RETURN_PATH_KEY, path);
    else store.removeItem(RETURN_PATH_KEY);
  } catch (err) {
    // Storage blocked (private mode): the user simply lands on the home page.
  }
}

/**
 * Reads and clears the path kept by rememberReturnPath, re-checking it with safeReturnPath.
 *
 * @param {Storage} [storage] - Defaults to sessionStorage.
 * @returns {string|null}
 */
export function takeRememberedReturnPath(storage) {
  const store = sessionStore(storage);
  if (!store) return null;
  try {
    const path = store.getItem(RETURN_PATH_KEY);
    store.removeItem(RETURN_PATH_KEY);
    return safeReturnPath(path);
  } catch (err) {
    return null;
  }
}
